import React, { useState } from 'react';
import { Copy, ArrowUp, ArrowDown, Trash2, ImageOff, ZoomIn, ZoomOut, RotateCcw, RotateCw, Circle, Square, RectangleHorizontal, Minus, Crop, ArrowRightLeft, Download } from 'lucide-react';
import { YearbookElement } from '@/types/yearbook';
import { HexColorPicker } from 'react-colorful';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface FloatingToolbarProps {
  selectedElements: YearbookElement[];
  position: { top: number; left: number };
  onDeleteElement: (id: string) => void;
  onDuplicateElement: (id: string) => void;
  onMoveLayer: (id: string, dir: 'up' | 'down') => void;
  onUpdateElement: (id: string, updates: Partial<YearbookElement>) => void;
  onDeleteMultiple?: (ids: string[]) => void;
  onDuplicateMultiple?: (ids: string[]) => void;
  onStartCrop?: (id: string) => void;
  onStartSlotSwap?: (id: string) => void;
}

const ZOOM_STEP = 0.15;
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ROTATION_STEP = 15;
const RADIUS_STEPS = [0, 8, 16, 28];
const STROKE_STEPS = [1, 2, 4, 6, 10];

interface TipBtnProps {
  label: string;
  onClick?: () => void;
  onDoubleClick?: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

const TipBtn: React.FC<TipBtnProps> = ({ label, onClick, onDoubleClick, disabled, className, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button onClick={onClick} onDoubleClick={onDoubleClick} disabled={disabled} className={className}>
        {children}
      </button>
    </TooltipTrigger>
    <TooltipContent side="bottom" className="text-xs">
      {label}
    </TooltipContent>
  </Tooltip>
);

const FloatingToolbar: React.FC<FloatingToolbarProps> = ({
  selectedElements,
  position,
  onDeleteElement,
  onDuplicateElement,
  onMoveLayer,
  onUpdateElement,
  onDeleteMultiple,
  onDuplicateMultiple,
  onStartCrop,
  onStartSlotSwap,
}) => {
  const isMulti = selectedElements.length > 1;
  const selectedElement = selectedElements[0];

  // For single selection — keep all existing logic
  const isSlotWithImage = !isMulti && selectedElement?.type === 'slot' && !!selectedElement?.imageUrl;
  const isImage = !isMulti && selectedElement?.type === 'image' && !selectedElement?.isSticker;
  const isShape = !isMulti && (selectedElement?.type === 'shape' || selectedElement?.type === 'shape_border');
  const isLine = !isMulti && selectedElement?.type === 'shape' && selectedElement?.shapeType === 'line';
  const isRotatable = !isMulti && (selectedElement?.type === 'image' || isShape || selectedElement?.type === 'text');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const shapeColor = selectedElement?.textStyle?.color || '#000000';

  const currentScale = selectedElement?.imageScale ?? 1;
  const canZoomIn = currentScale < ZOOM_MAX;
  const canZoomOut = currentScale > ZOOM_MIN;

  const currentRotation = selectedElement?.rotation ?? 0;

  const handleZoomIn = () => {
    if (!selectedElement) return;
    const next = Math.min(ZOOM_MAX, Math.round((currentScale + ZOOM_STEP) * 100) / 100);
    onUpdateElement(selectedElement.id, { imageScale: next });
  };

  const handleZoomOut = () => {
    if (!selectedElement) return;
    const next = Math.max(ZOOM_MIN, Math.round((currentScale - ZOOM_STEP) * 100) / 100);
    onUpdateElement(selectedElement.id, { imageScale: next });
  };

  const handleRotate = (delta: number) => {
    if (!selectedElement) return;
    const next = (currentRotation + delta + 360) % 360;
    onUpdateElement(selectedElement.id, { rotation: next });
  };

  const handleRotationReset = () => {
    if (!selectedElement) return;
    onUpdateElement(selectedElement.id, { rotation: 0 });
  };

  const handleCycleRadius = () => {
    if (!selectedElement) return;
    const current = selectedElement.borderRadius ?? 0;
    const idx = RADIUS_STEPS.indexOf(current);
    const next = RADIUS_STEPS[(idx + 1) % RADIUS_STEPS.length];
    onUpdateElement(selectedElement.id, { borderRadius: next });
  };

  const handleToggleCircle = () => {
    if (!selectedElement) return;
    if (!selectedElement.isCircle) {
      const minSide = Math.min(selectedElement.size.width, selectedElement.size.height);
      const dx = (selectedElement.size.width - minSide) / 2;
      const dy = (selectedElement.size.height - minSide) / 2;
      onUpdateElement(selectedElement.id, {
        isCircle: true,
        preCircleSize: { ...selectedElement.size },
        preCirclePosition: { ...selectedElement.position },
        position: {
          x: selectedElement.position.x + dx,
          y: selectedElement.position.y + dy,
        },
        size: { width: minSide, height: minSide },
      });
    } else {
      const restored = selectedElement.preCircleSize
        ? {
            isCircle: false,
            size: selectedElement.preCircleSize,
            position: selectedElement.preCirclePosition || selectedElement.position,
            preCircleSize: undefined,
            preCirclePosition: undefined,
          }
        : { isCircle: false };
      onUpdateElement(selectedElement.id, restored);
    }
  };

  const handleCycleStroke = () => {
    if (!selectedElement) return;
    const current = selectedElement.strokeWidth ?? 2;
    const idx = STROKE_STEPS.indexOf(current);
    const next = STROKE_STEPS[(idx + 1) % STROKE_STEPS.length];
    onUpdateElement(selectedElement.id, { strokeWidth: next });
  };

  const handleDeleteMulti = () => {
    if (onDeleteMultiple) {
      onDeleteMultiple(selectedElements.map((e) => e.id));
    }
  };

  const handleDuplicateMulti = () => {
    if (onDuplicateMultiple) {
      onDuplicateMultiple(selectedElements.map((e) => e.id));
    }
  };

  const handleDownloadImage = async () => {
    if (!selectedElement) return;
    const pictureId = selectedElement.pictureId;
    const fallbackUrl = selectedElement.imageUrl;
    try {
      let url: string | undefined;
      if (pictureId) {
        const res = await fetch(`/pictures/${pictureId}/get_original_file.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (res.ok) {
          const data = await res.json();
          url = data.url || data.original_url || data.file_url;
        }
      }
      if (!url) url = fallbackUrl;
      if (!url) throw new Error('No image url available');
      const imgRes = await fetch(url, { credentials: 'omit' });
      const blob = await imgRes.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];
      a.download = `photo-${pictureId ?? 'image'}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      console.error('Download image failed', e);
    }
  };


  const btnClass =
    'flex items-center justify-center p-1.5 rounded-md bg-background/80 hover:bg-muted transition-colors text-foreground';

  if (!selectedElement) return null;


  return (
    <TooltipProvider delayDuration={300}>
      <div
        className="absolute z-50 pointer-events-auto"
        style={{ top: position.top, left: position.left, transform: 'translateX(-50%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-1 px-1.5 py-1 rounded-lg bg-background/95 backdrop-blur-sm border border-border shadow-lg">
          {isMulti ? (
            /* ===== Multi-selection toolbar ===== */
            <>
              <span className="text-xs text-muted-foreground px-1 font-medium">{selectedElements.length} éléments</span>
              <div className="w-px h-5 bg-border mx-0.5" />

              <TipBtn label="Dupliquer tout" onClick={handleDuplicateMulti} className={btnClass}>
                <Copy size={14} />
              </TipBtn>

              <div className="w-px h-5 bg-border mx-0.5" />

              <TipBtn label="Supprimer tout" onClick={handleDeleteMulti} className="flex items-center justify-center p-1.5 rounded-md hover:bg-destructive/10 transition-colors text-destructive">
                <Trash2 size={14} />
              </TipBtn>
            </>
          ) : isSlotWithImage ? (
            <>
              <TipBtn label="Dézoomer la photo" onClick={handleZoomOut} disabled={!canZoomOut} className={`${btnClass} disabled:opacity-30`}>
                <ZoomOut size={14} />
              </TipBtn>

              <TipBtn label="Zoomer la photo" onClick={handleZoomIn} disabled={!canZoomIn} className={`${btnClass} disabled:opacity-30`}>
                <ZoomIn size={14} />
              </TipBtn>

              <div className="w-px h-5 bg-border mx-0.5" />

              <TipBtn label={selectedElement.isCircle ? 'Forme rectangulaire' : 'Forme ronde'} onClick={handleToggleCircle} className={btnClass}>
                {selectedElement.isCircle ? <Square size={14} /> : <Circle size={14} />}
              </TipBtn>

              {!selectedElement.isCircle && (
                <TipBtn label="Coins arrondis" onClick={handleCycleRadius} className={btnClass}>
                  <RectangleHorizontal size={14} style={{ borderRadius: (selectedElement.borderRadius ?? 0) > 0 ? 4 : 0 }} />
                </TipBtn>
              )}

              <TipBtn label="Déplacer vers un autre slot" onClick={() => onStartSlotSwap?.(selectedElement.id)} className={btnClass}>
                <ArrowRightLeft size={14} />
              </TipBtn>

              <TipBtn label="Télécharger la photo" onClick={handleDownloadImage} className={btnClass}>
                <Download size={14} />
              </TipBtn>

              <div className="w-px h-5 bg-border mx-0.5" />

              <TipBtn
                label="Retirer la photo"
                onClick={() =>
                  onUpdateElement(selectedElement.id, {
                    imageUrl: undefined,
                    imageRatio: undefined,
                    imageOffset: undefined,
                    imageScale: undefined,
                  })
                }
                className={btnClass}
              >
                <ImageOff size={14} className="text-destructive" />
              </TipBtn>
            </>
          ) : (
            <>
              {isLine && (
                <TipBtn label={`Épaisseur du trait (${selectedElement.strokeWidth ?? 2}px)`} onClick={handleCycleStroke} className={btnClass}>
                  <Minus size={14} strokeWidth={Math.min((selectedElement.strokeWidth ?? 2) * 1.5, 8)} />
                </TipBtn>
              )}

              {isImage && (
                <>
                  <TipBtn label="Dézoomer" onClick={handleZoomOut} disabled={!canZoomOut} className={`${btnClass} disabled:opacity-30`}>
                    <ZoomOut size={14} />
                  </TipBtn>
                  <TipBtn label="Zoomer" onClick={handleZoomIn} disabled={!canZoomIn} className={`${btnClass} disabled:opacity-30`}>
                    <ZoomIn size={14} />
                  </TipBtn>

                  <TipBtn label="Recadrer" onClick={() => onStartCrop?.(selectedElement.id)} className={btnClass}>
                    <Crop size={14} />
                  </TipBtn>

                  <TipBtn label="Télécharger la photo" onClick={handleDownloadImage} className={btnClass}>
                    <Download size={14} />
                  </TipBtn>

                  <div className="w-px h-5 bg-border mx-0.5" />

                  <TipBtn label={selectedElement.isCircle ? 'Forme rectangulaire' : 'Forme ronde'} onClick={handleToggleCircle} className={btnClass}>
                    {selectedElement.isCircle ? <Square size={14} /> : <Circle size={14} />}
                  </TipBtn>

                  {!selectedElement.isCircle && (
                    <TipBtn label="Coins arrondis" onClick={handleCycleRadius} className={btnClass}>
                      <RectangleHorizontal size={14} style={{ borderRadius: (selectedElement.borderRadius ?? 0) > 0 ? 4 : 0 }} />
                    </TipBtn>
                  )}
                </>
              )}

              {isRotatable && (
                <>
                  <div className="w-px h-5 bg-border mx-0.5" />
                  <TipBtn label="Rotation -15°" onClick={() => handleRotate(-ROTATION_STEP)} className={btnClass}>
                    <RotateCcw size={14} />
                  </TipBtn>
                  <TipBtn label="Double-clic pour réinitialiser" onDoubleClick={handleRotationReset} className="text-xs text-muted-foreground hover:text-foreground transition-colors min-w-[2.5rem] text-center font-mono cursor-pointer select-none">
                    {Math.round(currentRotation)}°
                  </TipBtn>
                  <TipBtn label="Rotation +15°" onClick={() => handleRotate(ROTATION_STEP)} className={btnClass}>
                    <RotateCw size={14} />
                  </TipBtn>
                </>
              )}

              <div className="w-px h-5 bg-border mx-0.5" />

              <TipBtn label="Dupliquer" onClick={() => onDuplicateElement(selectedElement.id)} className={btnClass}>
                <Copy size={14} />
              </TipBtn>

              <TipBtn label="Avancer" onClick={() => onMoveLayer(selectedElement.id, 'up')} className={btnClass}>
                <ArrowUp size={14} />
              </TipBtn>
              <TipBtn label="Reculer" onClick={() => onMoveLayer(selectedElement.id, 'down')} className={btnClass}>
                <ArrowDown size={14} />
              </TipBtn>

              <div className="w-px h-5 bg-border mx-0.5" />

              <TipBtn label="Supprimer" onClick={() => onDeleteElement(selectedElement.id)} className="flex items-center justify-center p-1.5 rounded-md hover:bg-destructive/10 transition-colors text-destructive">
                <Trash2 size={14} />
              </TipBtn>
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default FloatingToolbar;
